import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import JSONLD from '@/components/feature/JSONLD';
import { aboutTimeline, certifications, contactInfo } from '@/mocks/aboutData';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于开云登录',
  description: '开云登录品牌介绍，了解我们的发展历程、资质认证和联系方式。',
  url: 'https://www.kaiyun-login.com/about',
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '开云登录',
  url: 'https://www.kaiyun-login.com',
  description: '开云登录提供安全、快速、稳定的登录服务，支持多设备一键登录',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-0000',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CN',
    addressLocality: '北京',
    addressRegion: '海淀区',
    streetAddress: '中关村科技园区创新路88号开云大厦',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyun-login.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.kaiyun-login.com/about' },
  ],
};

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <JSONLD data={aboutSchema} />
      <JSONLD data={organizationSchema} />
      <JSONLD data={breadcrumbSchema} />
      <SEOHead
        title="关于我们 - 开云登录 | 品牌介绍与联系方式"
        description="了解开云登录的品牌故事、发展历程、资质认证和联系方式。开云登录致力于为用户提供安全、快速、稳定的登录服务，已服务超过5000万用户和200家企业。"
        keywords="开云登录,关于开云登录,开云登录品牌,开云登录联系方式,开云登录发展历程"
        canonicalUrl="https://www.kaiyun-login.com/about"
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>
        </div>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                关于开云登录
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
                开云登录成立于2018年，是一家专注于登录认证技术的服务平台。
                我们以安全为核心，以体验为导向，致力于为个人用户和企业客户提供安全、快速、稳定的登录服务。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100/80 mb-4">
                  <i className="ri-eye-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让每一次登录都安全无忧，用技术守护每一位用户的数字身份安全。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100/80 mb-4">
                  <i className="ri-focus-3-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为全球最受信赖的登录认证平台，引领无密码时代的身份认证标准。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100/80 mb-4">
                  <i className="ri-heart-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">核心价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  安全第一、用户至上、创新驱动、诚信为本，这是我们始终坚守的核心价值观。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                发展历程
              </h2>
              <p className="text-sm text-foreground-600">
                从创立到壮大，每一步都坚实有力
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200/70 md:-translate-x-0.5"></div>
              <div className="space-y-8">
                {aboutTimeline.map((item, index) => {
                  const isLeft = index % 2 === 0;
                  return (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row items-start gap-6 ${
                        isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="absolute left-4 md:left-1/2 w-2 h-2 rounded-full bg-primary-500 transform -translate-x-1/2 mt-2 z-10 ring-4 ring-background-50"></div>
                      <div className={`pl-12 md:pl-0 md:w-1/2 ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primary-100/80 text-primary-700 mb-2">
                          {item.year}
                        </span>
                      </div>
                      <div className={`pl-12 md:pl-0 md:w-1/2 ${isLeft ? 'md:pl-12' : 'md:pr-12'}`}>
                        <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                          <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                资质认证
              </h2>
              <p className="text-sm text-foreground-600">
                权威认证，值得信赖
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {certifications.map((cert) => (
                <div
                  key={cert.name}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center hover:border-primary-200/60 transition-all"
                >
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100/80 mb-4">
                    <i className={`${cert.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">
                    {cert.name}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                如有任何问题或合作意向，欢迎随时联系我们
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100/80 mb-3">
                    <i className="ri-phone-line text-lg text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">客服热线</h3>
                  <p className="text-sm text-foreground-600">{contactInfo.phone}</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100/80 mb-3">
                    <i className="ri-mail-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100/80 mb-3">
                    <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">公司地址</h3>
                  <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/80">
                    <i className="ri-time-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900">工作时间</h3>
                    <p className="text-sm text-foreground-600">{contactInfo.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}